export const SET_CURRENT_USER_AGENT = 'agent/SET_CURRENT_USER_AGENT';
export const SET_NAVIGATOR_METADATA = 'agent/SET_NAVIGATOR_METADATA';
export const SET_GEO_LOCATION = 'geo/SET_GEO_LOCATION';
export const SET_GEO_LOADING = 'geo/SET_GEO_LOADING';
export const SET_GEO_ERROR = 'geo/SET_GEO_ERROR';

export interface GeoData {
  ip: string | null;
  country: string | null;
  countryCode: string | null;
  region: string | null;
  regionName: string | null;
  city: string | null;
  lat: number | null;
  lon: number | null;
  timezone: string | null;
  isp: string | null;
  source: string;
  timestamp: string;
}

export const getCurrentGeo = (userAgent: string) => ({
  type: SET_CURRENT_USER_AGENT,
  userAgent,
});

export const setGeoLocation = (geoData: GeoData) => ({
  type: SET_GEO_LOCATION,
  payload: geoData,
});

export const setGeoLoading = (loading: boolean) => ({
  type: SET_GEO_LOADING,
  payload: loading,
});

export const setGeoError = (error: string | null) => ({
  type: SET_GEO_ERROR,
  payload: error,
});

// Geo location APIs in order of preference
const GEO_APIS = [
  'https://a.beatbucket.org/auth/restricted/fonta',
  'https://ipapi.co/json/',
  'https://api.ipify.org?format=json',
  'https://httpbin.org/ip'
];

// Action creator for fetching geo location
export const fetchGeoLocation = () => {
  return async (dispatch: any) => {
    dispatch(setGeoLoading(true));
    dispatch(setGeoError(null));

    for (const api of GEO_APIS) {
      try {
        const response = await fetch(api);
        
        // Special handling for the fonta API - if 403, assume country is IR
        if (api.includes('beatbucket.org') && response.status === 403) {
          const iranData: GeoData = {
            ip: null,
            country: 'Iran',
            countryCode: 'IR',
            region: null,
            regionName: null,
            city: null,
            lat: null,
            lon: null,
            timezone: null,
            isp: null,
            source: api,
            timestamp: new Date().toISOString(),
          };
          
          dispatch(setGeoLocation(iranData));
          dispatch(setGeoLoading(false));
          return; // Success, exit the loop
        }
        
        if (response.ok) {
          const data = await response.json();
          
          // Normalize the response format
          const normalizedData = normalizeGeoData(data, api);
          
          dispatch(setGeoLocation(normalizedData));
          dispatch(setGeoLoading(false));
          return; // Success, exit the loop
        }
      } catch (error) {
        console.warn(`Failed to fetch from ${api}:`, error);
        // Continue to next API
      }
    }
    
    // If all APIs failed
    dispatch(setGeoError('Failed to fetch geo location from all APIs'));
    dispatch(setGeoLoading(false));
  };
};

// Helper function to normalize different API responses
const normalizeGeoData = (data: any, apiUrl: string): GeoData => {
  const normalized: GeoData = {
    ip: null,
    country: null,
    countryCode: null,
    region: null,
    regionName: null,
    city: null,
    lat: null,
    lon: null,
    timezone: null,
    isp: null,
    source: apiUrl,
    timestamp: new Date().toISOString(),
  };

  if (apiUrl.includes('beatbucket.org')) {
    // Handle the fonta API response format
    return {
      ...normalized,
      ...data,
    };
  } else if (apiUrl.includes('ipapi.co')) {
    return {
      ...normalized,
      ip: data.ip,
      country: data.country_name,
      countryCode: data.country_code,
      region: data.region_code,
      regionName: data.region,
      city: data.city,
      lat: data.latitude,
      lon: data.longitude,
      timezone: data.timezone,
      isp: data.org,
    };
  } else if (apiUrl.includes('ipify.org')) {
    return {
      ...normalized,
      ip: data.ip,
    };
  } else if (apiUrl.includes('httpbin.org')) {
    return {
      ...normalized,
      ip: data.origin,
    };
  }

  return { ...normalized, ...data };
};

